'use client';

import useMarketEvent from '@/components/predic/hooks/use-market-event';
import { MarketCard, type MarketCardMarket } from '@/components/predic/cards/market';
import { useCreateChat } from '@/hooks/useCreateChat';
import ChatStatusModal from '@/components/predic/ui/ChatStatusModal';
import { useUserContextStore } from '@/stores/userContextStore';
import {
  findDisplayMarket,
  getMarketOutcome,
  getMarketStatus,
  isMarketResolved,
} from '@/components/predic/utils/market';
import type { EventData } from '@/components/predic/detail/market/types';

function toMarketCardData(eventData: EventData): MarketCardMarket {
  const { event, markets } = eventData;
  const displayMarket = findDisplayMarket(markets, event.closed === 1);
  const poster =
    event.image || event.icon || displayMarket?.market_icon || displayMarket?.market_image || '';
  const yesPrice = toNumber(displayMarket?.outcome_yes_price);
  const change24h = toNumber(displayMarket?.price_change_24h);
  const totalVolume = toNumber(event.volume_24hr);
  const groupItemTitle = displayMarket?.group_item_title || 'YES';

  return {
    marketEventId: String(event.id),
    imageSrc: poster,
    imageAlt: event.title,
    title: event.title,
    outcome: groupItemTitle,
    probability: yesPrice * 100,
    change: change24h * 100,
    changePeriod: '(24h)',
    volume: totalVolume,
    category: event.category || '',
    statusLabel: getMarketStatus(displayMarket),
    resolvedOutcome: isMarketResolved(displayMarket) ? getMarketOutcome(displayMarket) : undefined,
  };
}

function toNumber(value?: string | number | null) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
}

export default function MarketAskPage({ slug }: { slug: string }) {
  const setUserContext = useUserContextStore((state) => state.setUserContext);
  const { createChat, isCreating } = useCreateChat();
  const { eventData, isLoading } = useMarketEvent({ eventSlug: slug, enabled: !!slug });

  const event = eventData?.event;
  const questions = event?.recommended_questions || [];
  const marketCardData = eventData ? toMarketCardData(eventData) : null;

  const handleAsk = async (question: string) => {
    setUserContext({
      context_type: 'market',
      data: eventData,
    });
    const sourceId = event?.slug ? `market_${event.slug}` : '';
    await createChat({ message: question, source: sourceId });
  };

  if (!slug || (!isLoading && !eventData)) return null;

  return (
    <>
      <div className="max-w-190 mx-auto">
        <div className="flex flex-col items-end gap-5">
          {marketCardData && (
            <div className="w-full max-w-125 min-w-90">
              <MarketCard
                marketEventId={marketCardData.marketEventId || ''}
                market={marketCardData}
                linkEnabled={false}
              />
            </div>
          )}
          {questions.map((item) => (
            <p
              key={item}
              className="max-w-190 px-4 py-2 border border-[#1E1E1E] bg-[#121212] rounded-xl cursor-pointer hover:text-[#a772ff] hover:underline"
              onClick={() => handleAsk(item)}
            >
              {item}
            </p>
          ))}
        </div>
      </div>
      {isCreating && <ChatStatusModal />}
    </>
  );
}
